import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { SegmentExport, TSegmentExportState } from '@feathr/blackbox';
import { Pill } from '@feathr/components';

interface IExportStateChipProps {
  segmentExport: SegmentExport;
}

interface IExportStateDisplay {
  theme: string;
  indicatorVariant: 'solid' | 'outline';
}

/**
 * Per-state pill theme + indicator. `pending` and `exporting` share one label.
 * Labels are translated at the render site so static key extraction sees each `t()`.
 */
export const exportStateDisplay: Record<TSegmentExportState, IExportStateDisplay> = {
  pending: { theme: 'yellow', indicatorVariant: 'outline' },
  exporting: { theme: 'yellow', indicatorVariant: 'outline' },
  stopping: { theme: 'yellow', indicatorVariant: 'outline' },
  ready: { theme: 'green', indicatorVariant: 'solid' },
  failed: { theme: 'red', indicatorVariant: 'solid' },
  stopped: { theme: 'red', indicatorVariant: 'solid' },
  expired: { theme: 'neutral', indicatorVariant: 'solid' },
};

const FALLBACK_DISPLAY: IExportStateDisplay = { theme: 'neutral', indicatorVariant: 'solid' };

function getStateLabel(state: TSegmentExportState, t: TFunction): string {
  switch (state) {
    case 'pending':

    case 'exporting':
      return t('In progress');

    case 'stopping':
      return t('Stopping…');

    case 'ready':
      return t('Completed');

    case 'failed':
      return t('Failed');

    case 'stopped':
      return t('Cancelled');

    case 'expired':
      return t('Expired');

    default:
      return state;
  }
}

function ExportStateChip({ segmentExport }: Readonly<IExportStateChipProps>): JSX.Element {
  const { t } = useTranslation();
  const state = segmentExport.get('state');
  const display = exportStateDisplay[state] ?? FALLBACK_DISPLAY;

  return (
    <Pill indicatorVariant={display.indicatorVariant} theme={display.theme} variant={'secondary'}>
      {getStateLabel(state, t)}
    </Pill>
  );
}

export default observer(ExportStateChip);
